// History — the pure core (design_documents/history.md).
//
// An append-only log of what the user did with their library. Events are
// produced by diffing an item before/after each change (historyEventsFor), quick
// corrections fold into the previous event (mergeEvent), the past is
// reconstructed once from saved dates (reconstructEvents), and the screens group
// and filter the log (groupByMonth, filterEvents) and build the release archive
// (pastReleases). Nothing here touches storage.

import { statusLabel, awardLabel } from "../components/status-badge.js";

export const MEDIA_ROUTE = {
	videogame: "/games",
	book: "/books",
	tv: "/tv",
	anime: "/anime",
	movie: "/movies",
	comic: "/comics",
};

export const MEDIA_LABEL = {
	videogame: "Game",
	book: "Book",
	tv: "TV",
	anime: "Anime",
	movie: "Movie",
	comic: "Comic",
};

// Event kinds and the filter groups the Activity tab offers.
export const EVENT_KINDS = ["added", "removed", "engagement", "ownership", "rating", "award", "times", "seen"];
export const ACTIVITY_FILTERS = [
	{ value: "added", label: "Added", match: (e) => e.kind === "added" || e.kind === "removed" },
	{ value: "started", label: "Started", match: (e) => e.kind === "engagement" && STARTED.has(e.to_value) },
	{ value: "finished", label: "Finished", match: (e) => (e.kind === "engagement" && e.to_value === "done") || e.kind === "times" },
	{ value: "owned", label: "Ownership", match: (e) => e.kind === "ownership" },
	{ value: "rated", label: "Rated & awards", match: (e) => e.kind === "rating" || e.kind === "award" },
	{ value: "seen", label: "Episodes", match: (e) => e.kind === "seen" },
	{ value: "status", label: "Other status", match: (e) => e.kind === "engagement" && !STARTED.has(e.to_value) && e.to_value !== "done" },
];
const STARTED = new Set(["playing", "replaying", "poked"]);

// Quick corrections to the same field of the same item within this window fold
// into the previous event instead of adding another (decision 3).
export const COLLAPSE_MS = 2 * 60 * 1000;

// ---------- ids ----------

export function randomId() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
	});
}

// A deterministic uuid-shaped id from a string (two FNV-1a passes → 128 bits).
// Reconstructed events use it so every device backfilling the same past
// produces the SAME ids, and the cloud upsert keeps one copy.
export function stableId(key) {
	const hash = (seed) => {
		let h = BigInt(seed);
		for (const ch of String(key)) {
			h ^= BigInt(ch.codePointAt(0));
			h = (h * 0x100000001b3n) & 0xffffffffffffffffn;
		}
		return h.toString(16).padStart(16, "0");
	};
	const hex = hash(0xcbf29ce484222325n) + hash(0x84222325cbf29ce4n);
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-5${hex.slice(13, 16)}-${((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16)}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}

// ---------- diff ----------

function snapshot(mediaType, item) {
	return {
		item_id: item.id,
		media_type: mediaType,
		title: item.title ?? null,
		cover_url: item.cover_override_url || item.cover_url || null,
		route: `${MEDIA_ROUTE[mediaType] ?? ""}/${item.id}`,
	};
}

// Seen flags in a TV/anime override map ({ "2x5": { seen: true } } / { "2": {…} }).
function seenKeys(map) {
	return new Set(Object.entries(map ?? {}).filter(([, v]) => v?.seen === true).map(([k]) => k));
}

// The events one change produces. `before` null = added, `after` null = removed.
export function historyEventsFor(mediaType, before, after, now = new Date().toISOString()) {
	if (!before && !after) return [];
	const item = after ?? before;
	const base = () => ({ id: randomId(), at: now, ...snapshot(mediaType, item), detail: {}, reconstructed: false, deleted: false });
	if (!before) {
		return [{ ...base(), kind: "added", from_value: null, to_value: after.engagement_status ?? null, detail: { ownership: after.ownership_status ?? null } }];
	}
	if (!after) return [{ ...base(), kind: "removed", from_value: null, to_value: null }];

	const out = [];
	if (before.engagement_status !== after.engagement_status && after.engagement_status) {
		out.push({ ...base(), kind: "engagement", from_value: before.engagement_status ?? null, to_value: after.engagement_status });
	}
	if (before.ownership_status !== after.ownership_status && after.ownership_status) {
		out.push({
			...base(),
			kind: "ownership",
			from_value: before.ownership_status ?? null,
			to_value: after.ownership_status,
			detail: { formats: after.formats ?? [] },
		});
	}
	if ((before.rating ?? null) !== (after.rating ?? null)) {
		out.push({ ...base(), kind: "rating", from_value: before.rating == null ? null : String(before.rating), to_value: after.rating == null ? null : String(after.rating) });
	}
	const hadAwards = new Set(before.awards ?? []);
	for (const award of after.awards ?? []) {
		if (!hadAwards.has(award)) out.push({ ...base(), kind: "award", from_value: null, to_value: award });
	}
	if ((before.times_finished ?? null) !== (after.times_finished ?? null) && after.times_finished) {
		out.push({ ...base(), kind: "times", from_value: before.times_finished ?? null, to_value: after.times_finished });
	}
	if (mediaType === "tv" || mediaType === "anime") {
		const eps = [...seenKeys(after.episode_overrides)].filter((k) => !seenKeys(before.episode_overrides).has(k));
		const seasons = [...seenKeys(after.season_overrides)].filter((k) => !seenKeys(before.season_overrides).has(k));
		if (eps.length || seasons.length) {
			out.push({ ...base(), kind: "seen", from_value: null, to_value: null, detail: { episodes: eps, seasons } });
		}
	}
	return out;
}

// ---------- collapsing ----------

// Fold `next` into `prev` when it's a quick correction (same item, same kind,
// within COLLAPSE_MS). Returns { action: "append" } | { action: "replace", event }
// | { action: "drop", id } (the change undid itself: A→B→A).
export function mergeEvent(prev, next) {
	if (!prev || prev.deleted || prev.reconstructed) return { action: "append" };
	if (prev.item_id !== next.item_id || prev.kind !== next.kind) return { action: "append" };
	if (new Date(next.at) - new Date(prev.at) > COLLAPSE_MS) return { action: "append" };
	if (next.kind === "seen") {
		const uniq = (a, b) => [...new Set([...(a ?? []), ...(b ?? [])])];
		return {
			action: "replace",
			event: {
				...prev,
				at: next.at,
				detail: {
					episodes: uniq(prev.detail?.episodes, next.detail?.episodes),
					seasons: uniq(prev.detail?.seasons, next.detail?.seasons),
				},
			},
		};
	}
	if (next.kind === "award" || next.kind === "added" || next.kind === "removed") return { action: "append" };
	if ((prev.from_value ?? null) === (next.to_value ?? null)) return { action: "drop", id: prev.id };
	return { action: "replace", event: { ...prev, at: next.at, to_value: next.to_value, detail: next.detail ?? prev.detail } };
}

// ---------- reconstruction (decision 4) ----------

const midday = (dateStr) => (dateStr && /^\d{4}-\d{2}-\d{2}$/.test(dateStr) ? `${dateStr}T12:00:00.000Z` : dateStr);

// Events rebuilt from the dates items already carry: created_at → Added,
// date_first_completed → Finished, and last_engaged_date → Started for things in
// progress. Deterministic ids (stableId) so devices converge.
export function reconstructEvents(itemsByMedia) {
	const out = [];
	for (const [mediaType, items] of Object.entries(itemsByMedia ?? {})) {
		for (const item of items ?? []) {
			if (!item?.id) continue;
			const snap = snapshot(mediaType, item);
			const ev = (kind, at, to_value) => ({
				id: stableId(`${item.id}:${kind}:reconstructed`),
				at: midday(at),
				...snap,
				kind,
				from_value: null,
				to_value,
				detail: {},
				reconstructed: true,
				deleted: false,
			});
			if (item.created_at) out.push(ev("added", item.created_at, null));
			if (item.date_first_completed) out.push({ ...ev("engagement", item.date_first_completed, "done"), id: stableId(`${item.id}:finished:reconstructed`) });
			if (STARTED.has(item.engagement_status) && item.last_engaged_date) {
				out.push({ ...ev("engagement", item.last_engaged_date, item.engagement_status), id: stableId(`${item.id}:started:reconstructed`) });
			}
		}
	}
	return out;
}

// ---------- wording ----------

const OWNERSHIP_WORDS = {
	have_it: "Now own it",
	had_it: "No longer own it",
	want_it: "Want it",
	friend_has_it: "A friend has it",
	borrowed: "Borrowed it",
	not_owned: "Don't own it",
};

function episodesSummary(detail) {
	const eps = detail?.episodes ?? [];
	const seasons = detail?.seasons ?? [];
	const parts = [];
	if (seasons.length) parts.push(seasons.length === 1 ? `season ${seasons[0]}` : `${seasons.length} seasons`);
	if (eps.length === 1) {
		const [s, e] = eps[0].split("x");
		parts.push(`S${s} · E${e}`);
	} else if (eps.length > 1) {
		parts.push(`${eps.length} episodes`);
	}
	return parts.join(" + ");
}

// The one-line description of an event ("Finished", "Rated ★★★★", …).
export function eventLabel(e) {
	const media = e.media_type === "videogame" ? null : e.media_type;
	switch (e.kind) {
		case "added":
			return "Added to your library";
		case "removed":
			return "Removed from your library";
		case "engagement":
			if (e.to_value === "done") return media === "book" || media === "comic" ? "Finished reading" : media === "videogame" || !media ? "Finished" : "Finished watching";
			if (e.to_value === "playing") {
				const verb = media === "book" || media === "comic" ? "reading" : media === "tv" || media === "anime" || media === "movie" ? "watching" : "playing";
				return `Started ${verb}`;
			}
			return statusLabel(e.to_value, media);
		case "ownership": {
			const formats = (e.detail?.formats ?? []).map((f) => statusLabel(f)).join(", ");
			return `${OWNERSHIP_WORDS[e.to_value] ?? statusLabel(e.to_value, media)}${e.to_value === "have_it" && formats ? ` (${formats})` : ""}`;
		}
		case "rating":
			return e.to_value ? `Rated ${"★".repeat(Number(e.to_value))}` : "Rating cleared";
		case "award":
			return `Awarded “${awardLabel(e.to_value)}”`;
		case "times":
			return `Finished ${e.to_value} time${e.to_value === "1" ? "" : "s"}`;
		case "seen":
			return `Seen ${episodesSummary(e.detail)}`;
		default:
			return e.kind;
	}
}

// ---------- browsing ----------

export function filterEvents(events, { media = "", activity = "", query = "", itemId = null } = {}) {
	const q = query.trim().toLowerCase();
	const f = ACTIVITY_FILTERS.find((x) => x.value === activity);
	return (events ?? [])
		.filter((e) => !e.deleted)
		.filter((e) => !itemId || e.item_id === itemId)
		.filter((e) => !media || e.media_type === media)
		.filter((e) => !f || f.match(e))
		.filter((e) => !q || (e.title ?? "").toLowerCase().includes(q))
		.sort((a, b) => b.at.localeCompare(a.at));
}

// [{ key: "2026-10", label: "October 2026", items: [...] }], in input order.
export function groupByMonth(list, dateOf = (x) => x.at) {
	const groups = [];
	for (const x of list) {
		const d = dateOf(x) ?? "";
		const key = d.slice(0, 7);
		let g = groups[groups.length - 1];
		if (!g || g.key !== key) {
			const date = new Date(`${key}-01T00:00:00Z`);
			const label = Number.isNaN(date.getTime())
				? "Undated"
				: date.toLocaleDateString("en-GB", { month: "long", year: "numeric", timeZone: "UTC" });
			g = { key, label, items: [] };
			groups.push(g);
		}
		g.items.push(x);
	}
	return groups;
}

// ---------- releases (decision 7) ----------

export const RELEASE_KINDS = [
	{ value: "library", label: "In your library" },
	{ value: "episode", label: "New episodes" },
	{ value: "comic", label: "Comic issues" },
	{ value: "trade", label: "Collected editions" },
];

const NOTICE_KIND = {
	release: "library",
	episode: "episode",
	"comic-out": "comic",
	"comic-announced": "comic",
	"comic-trade": "trade",
};

// Things that have come out, newest first: every library item with a precise
// release date in the past, plus archived release notices (which also cover
// things not in the library — subscribed-series issues, new trades). A notice
// about a library item that's already listed is dropped.
//   → [{ id, date, title, media_type, cover_url, route, kind, note }]
export function pastReleases(allMedia, notifications, today) {
	const out = [];
	const listed = new Set();
	for (const m of allMedia ?? []) {
		if (!m.release_date || m.release_date > today) continue;
		listed.add(m.id);
		out.push({
			id: `lib:${m.id}`,
			date: m.release_date,
			title: m.title,
			media_type: m.media_type,
			cover_url: m.cover_url ?? null,
			route: m.route,
			kind: "library",
			note: m.mediaLabel ?? MEDIA_LABEL[m.media_type] ?? "",
		});
	}
	for (const n of notifications ?? []) {
		const kind = NOTICE_KIND[n.type];
		if (!kind) continue;
		if (n.type === "comic-announced") continue; // announced ≠ released
		if (n.item_id && listed.has(n.item_id)) continue;
		out.push({
			id: `notice:${n.id}`,
			date: (n.created_at ?? "").slice(0, 10),
			title: n.title,
			media_type: n.media_type ?? null,
			cover_url: n.cover_url ?? null,
			route: n.route ?? null,
			kind,
			note: n.body ?? "",
		});
	}
	return out.sort((a, b) => b.date.localeCompare(a.date) || a.title.localeCompare(b.title));
}
